/**
 * @file ProductGroupModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description A product group's window (Cam, 28 Oct 2026 — O1, "handle the
 *              editing / maintenance / creation of item grouping"): its name,
 *              category and notes; its items, each with the supplier it is
 *              bought from, its unit and pack, and its variant / additive
 *              label (blank = the plain product); add items (an item in
 *              another group is moved, and said); take one out; archive or
 *              restore the group; its history. Opened for a new group with
 *              `groupId` null — the group is made on Save, then items can be
 *              added. Changes need 'Edit inventory items' (`canEdit`); without
 *              it everything reads and the controls are greyed with why.
 *              Mounted only while open, so its slide-out buttons are measured
 *              with it (useSlideOutButton).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import SearchableDropdown from './SearchableDropdown';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import AuditHistoryModal from './AuditHistoryModal';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { groupNameProblem, packLabel, supplierLabel, errorOf } from '../utils/productGroups';
import { formatDate } from '../utils/dateFormatters';
import './ProductGroups.css';

const NO_EDIT = "Changing product groups needs 'Edit inventory items'";

function ProductGroupModal({ groupId: initialId = null, canEdit = false, onClose }) {
  const [groupId, setGroupId] = useState(initialId);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(!!initialId);
  const [form, setForm] = useState({ name: '', category: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [busyItem, setBusyItem] = useState(null);
  const [labels, setLabels] = useState({});
  const [itemOptions, setItemOptions] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirm, setConfirm] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useSlideOutButton([group, saving, groupId, canEdit]);

  const say = (message, type = 'error') => setAlert({ isOpen: true, message, type });

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/product-groups/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error(await errorOf(res, 'Could not read the group'));
      const data = await res.json();
      setGroup(data.group);
      setForm({ name: data.group.name || '', category: data.group.category || '', notes: data.group.notes || '' });
      setLabels(Object.fromEntries(data.group.members.map(m => [m.id, m.additive || ''])));
    } catch (e) {
      say(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(groupId); }, [groupId, load]);

  // The item picker: every live item, read once when the window opens.
  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/product-groups/item-options`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { items: [] }))
      .then(d => { if (live) setItemOptions(d.items || []); })
      .catch(() => { if (live) setItemOptions([]); });
    return () => { live = false; };
  }, []);

  const members = useMemo(() => (group ? group.members : []), [group]);
  const memberIds = useMemo(() => new Set(members.map(m => m.id)), [members]);
  const pickerOptions = useMemo(() => (itemOptions || [])
    .filter(i => !memberIds.has(i.id))
    .map(i => ({
      value: i.id,
      badge: i.item_code,
      label: i.item_name,
      tag: i.group ? `in ${i.group.name}` : null,
      renderLabel: (
        <span className="pg-pick">
          <span>{i.item_name}</span>
          <span className="pg-pick-meta">{[supplierLabel(i.supplier), packLabel(i.pack), i.unit].join(' · ')}</span>
        </span>
      ),
      searchText: `${i.item_code} ${i.item_name} ${i.supplier ? i.supplier.name : ''} ${i.group ? i.group.name : ''}`
    })), [itemOptions, memberIds]);

  const nameProblem = groupNameProblem(form.name);
  const dirty = !group
    ? !!form.name.trim()
    : (form.name.trim() !== (group.name || '') || (form.category || '') !== (group.category || '') || (form.notes || '') !== (group.notes || ''));

  const save = async () => {
    if (nameProblem) { say(nameProblem); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/product-groups${groupId ? `/${groupId}` : ''}`, {
        method: groupId ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, category: form.category, notes: form.notes })
      });
      if (!res.ok) throw new Error(await errorOf(res, 'Could not save the group'));
      const data = await res.json();
      setGroup(data.group);
      setGroupId(data.group.id);
      setForm({ name: data.group.name || '', category: data.group.category || '', notes: data.group.notes || '' });
      setLabels(Object.fromEntries(data.group.members.map(m => [m.id, m.additive || ''])));
    } catch (e) {
      say(e.message);
    } finally {
      setSaving(false);
    }
  };

  const afterItems = (data, words) => {
    setGroup(data.group);
    setLabels(Object.fromEntries(data.group.members.map(m => [m.id, m.additive || ''])));
    const moved = (data.moved || []).map(m => `${m.item_code} (from ${m.from_group})`);
    if (moved.length) say(`${words} — moved out of another group: ${moved.join(', ')}.`, 'info');
    // the picker's "in <group>" tags follow the change
    const changed = new Map((data.changed || []).map(c => [c.id, c]));
    setItemOptions(prev => (prev || []).map(i => {
      const c = changed.get(i.id);
      if (!c) return i;
      return { ...i, group: c.to ? { id: c.to, name: data.group && data.group.id === c.to ? data.group.name : (i.group ? i.group.name : '') } : null };
    }));
  };

  const addItem = async (itemId) => {
    if (!itemId || !groupId) return;
    setBusyItem(itemId);
    try {
      const res = await fetch(`${API_URL}/api/product-groups/${groupId}/items`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ itemId }] })
      });
      if (!res.ok) throw new Error(await errorOf(res, 'Could not add the item'));
      afterItems(await res.json(), 'Added');
    } catch (e) {
      say(e.message);
    } finally {
      setBusyItem(null);
    }
  };

  const saveLabel = async (m) => {
    const next = (labels[m.id] || '').trim();
    if (next === (m.additive || '')) return;
    setBusyItem(m.id);
    try {
      const res = await fetch(`${API_URL}/api/product-groups/${groupId}/items/${m.id}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additive: next || null })
      });
      if (!res.ok) throw new Error(await errorOf(res, 'Could not save the variant'));
      afterItems(await res.json(), 'Saved');
    } catch (e) {
      say(e.message);
      setLabels(prev => ({ ...prev, [m.id]: m.additive || '' }));
    } finally {
      setBusyItem(null);
    }
  };

  const removeItem = (m) => setConfirm({
    title: 'Take this item out of the group?',
    message: `${m.item_code} ${m.item_name} will be in no product group. Its item is not changed otherwise.`,
    confirmText: 'Take it out',
    onConfirm: async () => {
      setConfirm(null);
      setBusyItem(m.id);
      try {
        const res = await fetch(`${API_URL}/api/product-groups/${groupId}/items/${m.id}`, { method: 'DELETE', credentials: 'include' });
        if (!res.ok) throw new Error(await errorOf(res, 'Could not take the item out'));
        afterItems(await res.json(), 'Taken out');
      } catch (e) {
        say(e.message);
      } finally {
        setBusyItem(null);
      }
    }
  });

  const setArchived = (archived) => setConfirm({
    title: archived ? 'Archive this group?' : 'Restore this group?',
    message: archived
      ? `"${group.name}" leaves the list and Pricing & Costing stops using it. Its items stay in it, so restoring it brings them back.`
      : `"${group.name}" is used again, with its ${members.length} item${members.length === 1 ? '' : 's'}.`,
    confirmText: archived ? 'Archive' : 'Restore',
    onConfirm: async () => {
      setConfirm(null);
      setSaving(true);
      try {
        const res = await fetch(`${API_URL}/api/product-groups/${groupId}/archive`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ archived })
        });
        if (!res.ok) throw new Error(await errorOf(res, 'Could not change the group'));
        setGroup((await res.json()).group);
      } catch (e) {
        say(e.message);
      } finally {
        setSaving(false);
      }
    }
  });

  const editBlock = !canEdit ? NO_EDIT : group && group.is_archived ? 'Archived — restore the group to change it' : null;
  const packs = [...new Set(members.map(m => m.pack))];

  const footer = (
    <div className="buttons slideout pg-footer">
      {groupId && (
        <div>
          <button className="btn-slideout" onClick={() => setShowHistory(true)} title="This group's history">
            <div className="icon-wrapper"><span className="material-symbols-rounded">history</span></div>
            <span className="btn-label">History</span>
          </button>
        </div>
      )}
      {group && (
        <div>
          <button className="btn-slideout" data-write-action onClick={() => setArchived(!group.is_archived)}
            disabled={!canEdit || saving} title={!canEdit ? NO_EDIT : group.is_archived ? 'Use this group again' : 'Archive this group (its items stay in it)'}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">{group.is_archived ? 'unarchive' : 'archive'}</span></div>
            <span className="btn-label">{group.is_archived ? 'Restore' : 'Archive'}</span>
          </button>
        </div>
      )}
      <div className="pg-footer-gap" />
      <div>
        <button className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Close</span>
        </button>
      </div>
      <div>
        <button className="btn-slideout" data-write-action onClick={save}
          disabled={!!editBlock || saving || !dirty || !!nameProblem}
          title={editBlock || (nameProblem && form.name ? nameProblem : !dirty ? 'Nothing changed' : groupId ? 'Save the name, category and notes' : 'Make the group, then add its items')}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
          <span className="btn-label">{saving ? 'Saving…' : groupId ? 'Save' : 'Make group'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={onClose} size="lg" className="product-group"
      title={group ? group.name : groupId ? 'Product group' : 'New product group'} footer={footer}>
      {loading ? <LoadingSpinner /> : (
        <div className="pg-window">
          {group && group.is_archived && (
            <div className="pg-banner">This group is archived. Pricing & Costing does not use it; its items are still in it.</div>
          )}
          <div className="pg-fields">
            <label className="pg-field pg-field-name">
              <span>Group name</span>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Supreme Rearer 18 (bulk)" disabled={!!editBlock} />
            </label>
            <label className="pg-field">
              <span>Category</span>
              <Input value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                placeholder="e.g. Compounds" disabled={!!editBlock} />
            </label>
            <label className="pg-field pg-field-notes">
              <span>Notes</span>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                placeholder="Anything the next person should know" disabled={!!editBlock} />
            </label>
          </div>
          {group && (
            <div className="pg-meta">
              {group.created_by_name ? `Made by ${group.created_by_name}` : 'Made'} {formatDate(group.created_at)}
              {group.updated_at && group.updated_at !== group.created_at ? ` · last changed ${group.updated_by_name ? `by ${group.updated_by_name} ` : ''}${formatDate(group.updated_at)}` : ''}
              {group.review_ref ? ` · from the review (${group.review_ref.split('/').pop()})` : ''}
            </div>
          )}

          {groupId ? (
            <>
              <div className="pg-section-head">
                <h4>Items ({members.length})</h4>
                {packs.length > 1 && (
                  <span className="pg-warn" title="A group is meant to be one pack — costs are compared pack by pack">
                    <span className="material-symbols-rounded">warning</span>
                    {packs.length} packs in one group: {packs.map(packLabel).join(', ')}
                  </span>
                )}
              </div>
              <div className="pg-add" title={editBlock || 'Add an item — one in another group is moved here'}>
                <SearchableDropdown
                  options={pickerOptions}
                  value={null}
                  onChange={(id) => addItem(id)}
                  placeholder={itemOptions === null ? 'Loading items…' : 'Add an item…'}
                  searchPlaceholder="Code, name, supplier or group"
                  disabled={!!editBlock || itemOptions === null || !!busyItem}
                />
              </div>
              <div className="table-container pg-members">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Item</th>
                      <th>Supplier</th>
                      <th>Pack</th>
                      <th>Unit</th>
                      <th title="The additive or version this item is of the product; blank is the plain product">Variant / additive</th>
                      <th className="actions-col" />
                    </tr>
                  </thead>
                  <tbody>
                    {!members.length ? (
                      <tr><td colSpan="7" className="text-center">No items yet — add them above.</td></tr>
                    ) : members.map(m => (
                      <tr key={m.id} className={m.is_archived ? 'pg-archived-item' : ''}>
                        <td><span className="account-badge">{m.item_code}</span></td>
                        <td>{m.item_name}{m.is_archived && <span className="archived-badge">Archived</span>}</td>
                        <td>{supplierLabel(m.supplier)}</td>
                        <td>{packLabel(m.pack)}</td>
                        <td>{m.unit}</td>
                        <td className="pg-variant">
                          <Input value={labels[m.id] ?? ''} placeholder="Plain"
                            onChange={(e) => setLabels(prev => ({ ...prev, [m.id]: e.target.value }))}
                            onBlur={() => saveLabel(m)} onEnter={() => saveLabel(m)}
                            disabled={!!editBlock || busyItem === m.id} />
                        </td>
                        <td className="actions-col">
                          <div className="actions-cell">
                            <button className={`btn-icon${editBlock ? ' btn-disabled' : ''}`} data-write-action
                              onClick={() => removeItem(m)} disabled={!!editBlock || busyItem === m.id}
                              title={editBlock || 'Take this item out of the group'}>
                              <span className="material-symbols-rounded">remove_circle</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="pg-hint">Name the group and press <strong>Make group</strong>, then add its items. One group per product per pack — bulk, totes and 25kg bags are separate groups; additive versions go in their pack's group.</p>
          )}
        </div>
      )}

      <AlertDialog isOpen={alert.isOpen} message={alert.message} type={alert.type}
        onClose={() => setAlert(a => ({ ...a, isOpen: false }))} />
      <ConfirmDialog isOpen={!!confirm} title={confirm?.title} message={confirm?.message} type="warning"
        confirmText={confirm?.confirmText} onConfirm={() => confirm && confirm.onConfirm()} onCancel={() => setConfirm(null)} />
      {showHistory && groupId && (
        <AuditHistoryModal isOpen={showHistory} onClose={() => setShowHistory(false)}
          entityType="product_group" entityId={groupId} entityIdentifier={group ? group.name : ''} />
      )}
    </Modal>
  );
}

export default ProductGroupModal;
